import React, { useState } from "react";
import { useNavigate, Link, useLoaderData, Navigate } from "react-router-dom";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import logo from "../../../assets/mooring-mate-logo.png";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import styled from "@mui/system/styled";
import "./Login.css";
import { Copyright } from "../../../components";
import toast from "react-hot-toast";
import CircularProgress from "@mui/material/CircularProgress";
import { login } from "../../../services";

const useStyles = styled((theme) => ({
  root: {
    height: "100vh",
  },
  image: {
    backgroundImage:
      "url(https://images.unsplash.com/photo-1528154291023-a6525fabe5b4?ixid=MXwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHw%3D&ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80)",
    backgroundRepeat: "no-repeat",
    backgroundColor:
      theme.palette.type === "light"
        ? theme.palette.grey[50]
        : theme.palette.grey[900],
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  signinbtn: {
    boxShadow: "0 0px 0px 0px rgba(255, 255, 255, .0)!important",
    height: "50px",
    marginBottom: "12px",
    borderRadius: "10px",
    marginTop: "24px",
    background: "#2E5263",
    "&:hover": {
      background: "#214454",
    },
  },
  paper: {
    margin: theme.spacing(8, 4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

// markup
export const Login = () => {
  const user = useLoaderData();
  const classes = useStyles();
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setData({ ...data, [name]: value });
    return;
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    const res = await login(data);
    // console.log("res: ", res);

    if (res === null) {
      setLoading(false);
      toast.error("Something went wrong, please try again later.");
      return;
    }

    if (res?.status === false) {
      setLoading(false);
      toast.error(res?.message);
      return;
    }

    if (res?.status === true) {
      setLoading(false);

      const userData = {
        token: res?.token,
        data: res?.data,
      };

      if (remember) {
        localStorage.setItem("dockingmate", JSON.stringify(userData));
      } else {
        sessionStorage.setItem("dockingmate", JSON.stringify(userData));
      }

      navigate("/home");
      return;
    }

    setLoading(false);
    toast.error("Something went wrong, please try again later");
    return;
  };

  if (user) {
    const getPath = (role) => {
      return "/home";
    };

    return (
      <div className="loaderParent">
        <CircularProgress />
        <Navigate to={getPath(user?.data?.role)} replace={true} />
      </div>
    );
  }

  return (
    <>
      {/* <Header /> */}
      <Grid container className="loginContainer">
        {/* <CssBaseline /> */}
        <Grid
          item
          xs={false}
          sm={4}
          md={6}
          lg={7}
          className="imageContainer"
        ></Grid>
        <Grid item xs={12} sm={8} md={6} lg={5} className="formContainer">
          <div className="logo-section">
            <img
              className="landing-logo"
              src={logo}
              alt="Logo"
              onClick={() => navigate("/")}
            />
          </div>
          <h1 className="title">Sign In</h1>
          <form
            mt={1}
            sx={{
              width: "100%", // Fix IE 11 issue.
            }}
            onSubmit={handleSubmit}
          >
            <TextField
              variant="outlined"
              margin="normal"
              type="email"
              fullWidth
              size="small"
              id="email"
              required
              label="Email Address"
              name="userName"
              autoComplete="email"
              onChange={handleChange}
            />
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              name="Password"
              size="small"
              required
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  value={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  color="primary"
                />
              }
              label="Remember me"
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              className="theme-btn"
              loading={loading}
              loadingPosition="end"
              sx={{
                boxShadow: "0 0px 0px 0px rgba(255, 255, 255, .0)!important",
                height: "50px",
                marginBottom: "12px",
                borderRadius: "10px",
                marginTop: "10px",
              }}
            >
              Sign In
            </Button>
            <Grid container>
              <Grid item>
                <Link to="/signup" variant="body2" className="link">
                  {"Don't have an account? Sign Up"}
                </Link>
              </Grid>
            </Grid>
            <Grid container mt={1}>
              <Grid item>
                <Link to="/forgot-password" variant="body2" className="link">
                  {"Forgot password? Reset"}
                </Link>
              </Grid>
            </Grid>
            <Box mt={5}>
              <Copyright />
            </Box>
          </form>
        </Grid>
      </Grid>
    </>
  );
};
